import { useState, useEffect, useRef, useCallback } from 'react';
import { AIFieldContext } from '../components/AIAssistantContexts';
import { getSettings, PVQSettings } from '../utils/settings';


interface AutoAIAssistHookProps {
  fieldContext: AIFieldContext;
  currentValue: string;
  onAutoEngage: (reason: string) => void;
  onPositiveFeedback: () => void;
  isAIAssistActive?: () => boolean;
}

interface AutoAIAssistHookResult {
  isChecking: boolean;
  lastCheckResult: 'pending' | 'adequate' | 'inadequate' | null;
  shouldShowPositiveFeedback: boolean;
}

export const useAutoAIAssist = ({
  fieldContext,
  currentValue,
  onAutoEngage,
  onPositiveFeedback,
  isAIAssistActive
}: AutoAIAssistHookProps): AutoAIAssistHookResult => {
  const [settings, setSettings] = useState<PVQSettings>(getSettings());
  
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckResult, setLastCheckResult] = useState<'pending' | 'adequate' | 'inadequate' | null>(null);
  const [shouldShowPositiveFeedback, setShouldShowPositiveFeedback] = useState(false);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastValueRef = useRef(currentValue);
  const hasShownFeedbackRef = useRef(false);
  const lastCheckedContentRef = useRef<string>('');
  const lastCheckWasAdequateRef = useRef(false);

  // Load settings from localStorage
  useEffect(() => {
    const loadSettings = () => {
      setSettings(getSettings());
    };

    // Load initial settings
    loadSettings();

    // Listen for settings changes
    window.addEventListener('pvq-settings-changed', loadSettings);

    return () => {
      window.removeEventListener('pvq-settings-changed', loadSettings);
    };
  }, []);

  // Reset feedback and check state when value changes significantly
  useEffect(() => {
    const significantChange = Math.abs(currentValue.length - lastValueRef.current.length) > 10;
    const contentChanged = currentValue.trim() !== lastCheckedContentRef.current.trim();
    
    if (significantChange || contentChanged) {
      setShouldShowPositiveFeedback(false);
      hasShownFeedbackRef.current = false;
      // Reset the last check state to allow re-checking
      lastCheckWasAdequateRef.current = false;
    }
    lastValueRef.current = currentValue;
  }, [currentValue]);

  const checkFieldAdequacy = useCallback(async () => {
    if (isChecking) return;
    
    setIsChecking(true);
    setLastCheckResult('pending');

    try {
      const adequacyResult = await assessFieldAdequacy(
        fieldContext,
        currentValue,
        settings.aiValidationThreshold
      );

      setLastCheckResult(adequacyResult.isAdequate ? 'adequate' : 'inadequate');
      
      // Store the checked content and result
      lastCheckedContentRef.current = currentValue.trim();
      lastCheckWasAdequateRef.current = adequacyResult.isAdequate;

      if (adequacyResult.isAdequate) {
        // Show positive feedback if enabled and not already shown
        if (settings.encouragingFeedback && !hasShownFeedbackRef.current) {
          setShouldShowPositiveFeedback(true);
          hasShownFeedbackRef.current = true;
          onPositiveFeedback();
          
          // Hide positive feedback after 3 seconds
          setTimeout(() => {
            setShouldShowPositiveFeedback(false);
          }, 3000);
        }
      } else {
        // Auto-engage AI Assist
        onAutoEngage(adequacyResult.reason);
      }
    } catch (error) {
      console.error('Error checking field adequacy:', error);
      setLastCheckResult(null);
      lastCheckWasAdequateRef.current = false;
    } finally {
      setIsChecking(false);
    }
  }, [fieldContext, currentValue, settings.aiValidationThreshold, settings.encouragingFeedback, isChecking, onAutoEngage, onPositiveFeedback]);

  // Auto-check logic
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only proceed if auto-check is enabled
    if (!settings.autoCheckFieldCompleteness) {
      return;
    }

    // Skip if AI Assistant is currently active
    if (isAIAssistActive && isAIAssistActive()) {
      return;
    }

    // Skip if value is too short to be meaningful
    if (currentValue.trim().length < 10) {
      setLastCheckResult(null);
      setShouldShowPositiveFeedback(false);
      return;
    }

    // Skip if content hasn't changed and last check was adequate
    if (currentValue.trim() === lastCheckedContentRef.current && lastCheckWasAdequateRef.current) {
      return;
    }

    // Set up delayed check
    timeoutRef.current = setTimeout(() => {
      // Double-check AI Assistant isn't active before proceeding
      if (isAIAssistActive && isAIAssistActive()) {
        return;
      }
      
      // Double-check content hasn't been checked and found adequate
      if (currentValue.trim() === lastCheckedContentRef.current && lastCheckWasAdequateRef.current) {
        return;
      }
      
      checkFieldAdequacy();
    }, 3000); // 3 seconds of inactivity

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentValue, settings.autoCheckFieldCompleteness, settings.aiValidationThreshold, checkFieldAdequacy, isAIAssistActive]);

  return {
    isChecking,
    lastCheckResult,
    shouldShowPositiveFeedback
  };
};

// Helper function to assess field adequacy using AI
const assessFieldAdequacy = async (
  fieldContext: AIFieldContext,
  content: string,
  threshold: 'low' | 'medium' | 'high'
): Promise<{ isAdequate: boolean; reason: string; confidence: number }> => {
  const endpoint = process.env.REACT_APP_AI_ASSIST_ENDPOINT || 'https://20n73zcs4g.execute-api.us-east-1.amazonaws.com/prod/assist';
  
  const thresholdPrompts = {
    low: 'Check if this response meets basic completeness requirements.',
    medium: 'Assess if this response is complete and reasonably detailed.',
    high: 'Evaluate if this response is comprehensive, detailed, and meets high-quality standards.'
  };

  const payload = {
    field_context: {
      ...fieldContext,
      validation_prompt: `${thresholdPrompts[threshold]} 
      
      Content to evaluate: "${content}"
      
      Respond with JSON format:
      {
        "is_adequate": boolean,
        "reason": "Brief explanation of why this content is/isn't adequate",
        "confidence": number between 0.0 and 1.0,
        "suggestions": ["Quick suggestions for improvement if inadequate"]
      }
      
      Consider these factors:
      - Length and detail level
      - Specificity and clarity
      - Completeness for security clearance purposes
      - Common rejection reasons: ${fieldContext.commonRejectionReasons.join(', ')}
      
      Be appropriately strict based on the ${threshold} threshold setting.`
    },
    user_input: content,
    interaction_type: 'adequacy_check'
  };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error(`Adequacy check failed: ${response.status}`);
  }

  const result = await response.json();
  
  // Parse the AI response
  let adequacyData;
  try {
    if (result.success && result.guidance) {
      // Try to parse JSON from the AI response
      const responseText = result.guidance.response || result.guidance.message || JSON.stringify(result.guidance);
      
      // Look for JSON in the response
      const jsonMatch = responseText.match(/\{.*\}/s);
      if (jsonMatch) {
        adequacyData = JSON.parse(jsonMatch[0]);
      } else {
        // Fallback: assume inadequate if we can't parse
        adequacyData = {
          is_adequate: false,
          reason: "Unable to assess adequacy properly",
          confidence: 0.5
        };
      }
    } else {
      throw new Error('Invalid AI response format');
    }
  } catch (parseError) {
    console.error('Error parsing adequacy response:', parseError);
    // Conservative fallback
    adequacyData = {
      is_adequate: false,
      reason: "Response may need more detail for security clearance review",
      confidence: 0.6
    };
  }

  return {
    isAdequate: adequacyData.is_adequate || false,
    reason: adequacyData.reason || 'Response could be more comprehensive',
    confidence: adequacyData.confidence || 0.5
  };
};